import { Button, Popup, Skeleton, TextArea, Toast } from "antd-mobile";
import { CheckOutline, LeftOutline } from "antd-mobile-icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getQualityRecord, MobileQualityRecord, submitQualityFeedback } from "../../../api/mobileQuality";
import styles from "./index.module.css";

const QualityDetail = () => {
  const navigate = useNavigate();
  const { recordId = "" } = useParams();
  const [record, setRecord] = useState<MobileQualityRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    getQualityRecord(recordId)
      .then(setRecord)
      .catch(() => Toast.show({ content: "品质详情加载失败" }))
      .finally(() => setLoading(false));
  }, [recordId]);

  const submitFeedback = async (agreed: boolean, feedbackNote?: string) => {
    if (!recordId || submitting) return;
    setSubmitting(true);
    try {
      const updated = await submitQualityFeedback(recordId, agreed, feedbackNote);
      setRecord(updated);
      setRejectPopup(false);
      setNote("");
      Toast.show({ content: agreed ? "已认可 AI 判定" : "已记录不认可反馈", icon: "success" });
    } catch (error) {
      Toast.show({ content: "反馈提交失败" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className={styles.page}><Skeleton.Title animated /><Skeleton.Paragraph lineCount={8} animated /></main>;
  }
  if (!record) {
    return <main className={styles.page}><div className={styles.empty}>记录不存在或无权查看</div></main>;
  }

  const isQualified = record.qualityResult === "QUALIFIED";
  const isCaptureIssue = record.captureStatus === "INVALID_CAPTURE";
  const feedbackText = record.aiJudgeFeedback === 1
    ? "已认可 AI 判定"
    : record.aiJudgeFeedback === 0 ? "已不认可 AI 判定" : "尚未人工复核";

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button aria-label="返回" onClick={() => navigate(-1)}><LeftOutline /></button>
        <div><span>{record.storeName || "-"}</span><h1>{record.dishName || "品质详情"}</h1></div>
        <i />
      </header>

      <div className={styles.scroll}>
        <section className={styles.capture}>
          <img src={record.captureImageUrl || ""} alt="菜品实拍" />
          <span>实拍 · {record.capturedAt ? dayjs(record.capturedAt).format("MM-DD HH:mm") : "-"}</span>
        </section>

        {isCaptureIssue ? (
          <section className={`${styles.resultCard} ${styles.captureIssue}`}>
            <small>拍摄状态</small><h2>无效拍摄</h2>
            <p>当前系统仅确认该图片不是有效菜品图片，不细分曝光、模糊或取景问题。</p>
          </section>
        ) : (
          <section className={styles.comparison}>
            {record.standardImageUrl
              ? <img src={record.standardImageUrl} alt="当前标准图" />
              : <div className={styles.noStandard}>暂无标准图</div>}
            <div>
              <small>AI 品质结论</small>
              <h2 className={isQualified ? styles.qualified : styles.unqualified}>{isQualified ? "达标" : "不达标"}</h2>
              {record.temperatureCelsius != null && <span>出餐温度 {record.temperatureCelsius.toFixed(1)}°C</span>}
              <p>{record.aiReason || "AI 未返回具体原因"}</p>
            </div>
          </section>
        )}

        {!isCaptureIssue && record.qualityResult && (
          <section className={styles.feedback}>
            <div className={styles.feedbackHead}>
              <div><h2>是否认可 AI 结果？</h2><span>反馈不会修改 AI 原始评分</span></div>
              <em className={record.aiJudgeFeedback == null ? styles.pending : styles.done}>{feedbackText}</em>
            </div>
            {record.aiJudgeFeedbackNote && <p className={styles.note}>备注：{record.aiJudgeFeedbackNote}</p>}
            <div className={styles.actions}>
              <Button loading={submitting} className={styles.agree} onClick={() => submitFeedback(true)}>
                <CheckOutline /> 认可 AI
              </Button>
              <Button disabled={submitting} className={styles.reject} onClick={() => { setNote(record.aiJudgeFeedbackNote || ""); setRejectPopup(true); }}>
                不认可
              </Button>
            </div>
          </section>
        )}
      </div>

      <Popup visible={rejectPopup} onMaskClick={() => setRejectPopup(false)} bodyClassName={styles.popup}>
        <h2>不认可 AI 判定</h2>
        <p>备注为选填，可说明菜名、颜色、份量或标准图等问题。</p>
        <TextArea value={note} onChange={setNote} maxLength={500} rows={5} showCount placeholder="请输入备注（选填）" />
        <div className={styles.popupActions}>
          <Button onClick={() => setRejectPopup(false)}>取消</Button>
          <Button color="primary" loading={submitting} onClick={() => submitFeedback(false, note)}>提交</Button>
        </div>
      </Popup>
    </main>
  );
};

export default QualityDetail;
